/**
 * Sewing Factory Board — the filter declaration, and the URL state that is not a filter.
 *
 * Mirrors the `filters:` block of `winsight-server`'s `reports/sewing-factory-board/report.yaml`,
 * which is authoritative: `factory`, `tran_date` and `metric` are required; `building` and `floor`
 * are multi-select (empty = all, §5.4 `_any`). Options share the floor board's `SEWING_FLOORS`
 * catalogue. Auto-refresh (120s, including background) is configured in `index.tsx`, not here.
 */

import { z } from 'zod';

import { FACTORY_OPTIONS, type FactoryCode } from '@/lib/factories';
import type { FilterOption, FilterSchema } from '@/lib/filters';
import {
  buildingsFor,
  defaultDate,
  floorsFor,
} from '@/reports/sewing-floor-board/filters';

/** Same board-day rule as the floor board (09:00 rollover, skip Sunday). */
export { defaultDate };

function options(values: readonly string[]): readonly FilterOption[] {
  return values.map((value) => ({ value, label: value }));
}

/**
 * The filter bar for one factory and one day.
 * Call through `useMemo` keyed on the factory — a fresh array every render re-parses the URL every render.
 *
 * Building and floor multi-selects default to empty (All) so the board opens factory-wide; the
 * reader unchecks or picks a subset to hide Sample Room / SUBCON / a building without leaving
 * the factory board.
 */
export function sewingFactoryBoardFilters(factory: FactoryCode): FilterSchema {
  return [
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      required: true,
      defaultValue: factory,
      options: FACTORY_OPTIONS,
    },
    {
      type: 'date',
      name: 'tran_date',
      label: 'Day',
      required: true,
      defaultValue: defaultDate,
    },
    {
      type: 'multi-select',
      name: 'building',
      label: 'Building',
      anyLabel: 'All buildings',
      options: options(buildingsFor(factory)),
    },
    {
      type: 'multi-select',
      name: 'floor',
      label: 'Floor',
      anyLabel: 'All floors',
      options: options(floorsFor(factory)),
    },
  ];
}

/**
 * Which KPI family the board shows — `EINS` passed end-of-line QC, `SFTS` is the last sewing operation.
 * A declared server filter: it chooses which measure the queries return, not which floors are in the answer.
 */
export const METRIC_PARAM = 'metric';
const metricSchema = z.enum(['EINS', 'SFTS']);
export type Metric = z.infer<typeof metricSchema>;

export function parseMetric(raw: string | null): Metric {
  const parsed = metricSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'EINS';
}

export const METRIC_TABS: readonly { value: Metric; label: string; hint: string }[] = [
  { value: 'EINS', label: 'Endline passed', hint: 'Pieces that passed end-of-line QC' },
  { value: 'SFTS', label: 'Sewing last operation', hint: 'Pieces off the last sewing operation' },
];

/**
 * Legacy's 120-second interval. Always on — see `index.tsx` `WALL_POLL` and design-notes
 * Auto-refresh. Matches the floor board; not an opt-in like Sewing Efficiency.
 */
export const REFRESH_INTERVAL_MS = 120 * 1000;
